import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';

/**
 * Gets the display name for an integration with fallbacks
 *
 * @param integration - The integration (book) descriptor
 * @returns The display name, falling back to name, then 'Unknown'
 */
export const getIntegrationDisplayName = (
  integration: Bdkv2BookDescriptor,
): string => {
  return integration.display_name || integration.name || 'Unknown';
};

/**
 * Gets the description for an integration with fallbacks
 *
 * @param integration - The integration (book) descriptor
 * @returns The description, preferring short description, falling back to long description, then a default message
 */
export const getIntegrationDescription = (
  integration: Bdkv2BookDescriptor,
): string => {
  return (
    integration.short_description ||
    integration.long_description ||
    'No description available'
  );
};

/**
 * Generates initials from a name (max 2 characters), skipping words that don't
 * start with a letter or digit, e.g. "&"
 *
 * @param name - The name to generate initials from
 * @returns The initials (uppercase, max 2 characters)
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim() === '') {
    return '';
  }

  return name
    .split(/\s+/)
    .filter(word => /^[\p{L}\p{N}]/u.test(word))
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};
